import { call, put, select } from 'redux-saga/effects';
import serviceLocator, { SSAP_SVC } from '../../APIs/common/service-locator';
import {
    cachePreviousSSNs,
    cacheSSNs,
    receiveSSNsFailure,
    receiveSSNsSuccess,
    startReceiveSSNs,
    updateSSNs,
    updatePreviousSSNs
} from '../../actions/ssap/ssapActionCreators';

import {
    getApplicantGuids,
    getApplicationId,
    getCachedData,
    getPreviousApplicantGuids,
    getPreviousApplicationId,
    getActivePageName
} from '../../reducers/appReducer';

import { isEqual } from '../../utils/miscellaneous/objectHelpers';

import { PAGES } from '../../constants/ssap/flows/pagesTypes';

export function* executeSagaCallbacks(callbacks, status) {
    if(callbacks) {
        const {
            updateStatus,
            toggleExtraDataStatus,
            togglePageReadiness,
        } = callbacks;

        if(typeof updateStatus === 'function' ) {
            yield call(updateStatus, status);
        }

        // #ToDo: Refactor to use status directly instead of using boolean flags
        if(typeof toggleExtraDataStatus === 'function') {
            yield call(toggleExtraDataStatus, status === 'started' ? false : true);
        }

        if(typeof togglePageReadiness === 'function') {
            yield call(togglePageReadiness, status === 'started' ? false : true);
        }
    }
}

export const checkCachedSSNs = (guids, cachedSsns) => {
    return isEqual(guids, Object.keys(cachedSsns), true); // Here we need to make sure it works if they have different order
};

export const isResponseValid = (response, guids) => {
    if(!response) {
        return false;
    }

    const guidsFromResponse = response.map(r => r.applicantGuid);

    return isEqual(guidsFromResponse, guids, true);
};

export const makeSsnsPayloadFromResponse = data => {
    return data
        .reduce(
            (ssns, applicant) => ({
                ...ssns,
                [applicant.applicantGuid]: applicant.ssn
            }),
            { }
        );
};


export function* receiveSsnsSaga(action) {
    try {
        const { callbacks } = action;

        const cachedData = yield select(getCachedData);
        const currentApplicationId = yield select(getApplicationId);
        const currentGuids = yield select(getApplicantGuids);
        const previousApplicationId = yield select(getPreviousApplicationId);
        const previousGuids = yield select(getPreviousApplicantGuids);
        const sagaCaller = yield select(getActivePageName);

        const isPersonalInformationPage = sagaCaller === PAGES.PERSONAL_INFORMATION_PAGE;

        const currentSsnsAlreadyCached = yield call(checkCachedSSNs, currentGuids, cachedData.ssns);
        const previousSsnsAlreadyCached = yield call(checkCachedSSNs, previousGuids, cachedData.previousSSNs);

        switch(sagaCaller) {
            case PAGES.PERSONAL_INFORMATION_PAGE:
            case PAGES.FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE:
            case PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE:

                if(currentSsnsAlreadyCached) {
                    yield put(updateSSNs(cachedData.ssns, true));
                    yield call(executeSagaCallbacks, callbacks, 'completed');
                    return;
                } else {
                    if(isPersonalInformationPage) {
                        yield put(startReceiveSSNs());
                    } else {
                        yield call(executeSagaCallbacks, callbacks, 'started');
                    }

                    const ssapService = yield call(serviceLocator, SSAP_SVC);

                    const response = yield call(
                        [
                            ssapService,
                            ssapService.receiveSSNs
                        ],
                        { ssapApplicationId: currentApplicationId }
                    );

                    if (isResponseValid(response, currentGuids)) {
                        const ssns = makeSsnsPayloadFromResponse(response);
                        yield put(updateSSNs(ssns));
                        yield put(cacheSSNs(ssns));
                        yield call(executeSagaCallbacks, callbacks, 'completed');

                        if(isPersonalInformationPage) {
                            yield put(receiveSSNsSuccess());
                        }

                        return;
                    } else {
                        if(isPersonalInformationPage) {
                            yield put(receiveSSNsFailure());                        // critical failure, hard stop
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed');  // here we will fail silently
                        }
                    }
                }
                break;

            case PAGES.FINAL_APPLICATION_REVIEW_PAGE:
                if (currentSsnsAlreadyCached && previousSsnsAlreadyCached) {
                    yield put(updateSSNs(cachedData.ssns, true));
                    yield put(updatePreviousSSNs(cachedData.previousSSNs));
                    yield call(executeSagaCallbacks, callbacks, 'completed');
                    return;
                } else {    
                    if(currentSsnsAlreadyCached) {
                        yield call(executeSagaCallbacks, callbacks, 'started');

                        // We have current SSNs cached, but not previous ones
                        const ssapService = yield call(serviceLocator, SSAP_SVC);

                        const response = yield call(
                            [
                                ssapService,
                                ssapService.receiveSSNs
                            ],
                            { ssapApplicationId: previousApplicationId }
                        );

                        if (isResponseValid(response, previousGuids)) {
                            const previousSsns = makeSsnsPayloadFromResponse(response);

                            yield put(updateSSNs(cachedData.ssns));                     // update SSNs in current app [dataReducer]
                            yield put(updatePreviousSSNs(previousSsns));                // update SSNs in previous app [previousDataReducer]
                            yield put(cachePreviousSSNs(previousSsns));                 // cache previous SSNs [cachedDataReducer.previousSSNs]
                            yield call(executeSagaCallbacks, callbacks, 'completed');
                            return;
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed');      // here we will fail silently
                        }
                    }

                    if(previousSsnsAlreadyCached) {
                        yield call(executeSagaCallbacks, callbacks, 'started');

                        // We have previous SSNs cached, but not current ones
                        const ssapService = yield call(serviceLocator, SSAP_SVC);

                        const response = yield call(
                            [
                                ssapService,
                                ssapService.receiveSSNs
                            ],
                            { ssapApplicationId: currentApplicationId }
                        );

                        if (isResponseValid(response, currentGuids)) {
                            const ssns = makeSsnsPayloadFromResponse(response);

                            yield put(updateSSNs(ssns));                                // update SSNs in current app [dataReducer]
                            yield put(updatePreviousSSNs(cachedData.previousSSNs));     // update SSNs in previous app [previousDataReducer]
                            yield put(cacheSSNs(ssns));                                 // cache current SSNs [cachedDataReducer.ssns]
                            yield call(executeSagaCallbacks, callbacks, 'completed');
                            return;
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed');      // here we will fail silently
                        }
                    }

                    if( !currentSsnsAlreadyCached && !previousSsnsAlreadyCached ) {
                        yield call(executeSagaCallbacks, callbacks, 'started');

                        // We do not have any cached values, so we need to fetch both current and previous SSNs
                        const ssapService = yield call(serviceLocator, SSAP_SVC);

                        const [ currentResponse, previousResponse ] = yield Promise.all(
                            [
                                yield call(
                                    [
                                        ssapService,
                                        ssapService.receiveSSNs
                                    ],
                                    { ssapApplicationId: currentApplicationId }
                                ),

                                yield call(
                                    [
                                        ssapService,
                                        ssapService.receiveSSNs
                                    ],
                                    { ssapApplicationId: previousApplicationId }
                                )
                            ]
                        );

                        const isCurrentResponseValid = isResponseValid(currentResponse, currentGuids);
                        const isPreviousResponseValid = isResponseValid(previousResponse, previousGuids);

                        if (isCurrentResponseValid && isPreviousResponseValid) {
                            const ssns = makeSsnsPayloadFromResponse(currentResponse);
                            const previousSsns = makeSsnsPayloadFromResponse(previousResponse);

                            yield put(updateSSNs(ssns));                                // update SSNs in current app [dataReducer]
                            yield put(updatePreviousSSNs(previousSsns));                // update SSNs in previous app [previousDataReducer]
                            yield put(cacheSSNs(ssns));                                 // cache current SSNs [cachedDataReducer.ssns]
                            yield put(cachePreviousSSNs(previousSsns));                 // cache previous SSNs [cachedDataReducer.previousSSNs]
                            yield call(executeSagaCallbacks, callbacks, 'completed');
                            return;
                        } else {
                            yield call(executeSagaCallbacks, callbacks, 'failed');      // here we will fail silently
                        }
                    }
                }
                break;

        }
    } catch (e) {
        yield call([console, console.error], e);
    }
}